import * as z from "zod";
import type { ReportType } from "../../../../api/client/types/catalog.types";
import type { MaintenanceType } from "../../../../api/client/types/catalog.types";
import type {
  PoolRegulation,
  InterventionLevel,
  AdverseEventType,
} from "../../../../api/client/types/catalog.types";
import type { Swimmer } from "../../../../api/client/types/swimmer.types";
import {
  isMaintenanceReportType,
  isPoolRegulationReportType,
  isAdverseEventReportType,
  isFirstAidStationReportType,
  ADVERSE_EVENT_TYPE_NAMES,
  isAdverseEventTypeValue,
  extractAdverseEventTypeId,
  isLlamadoDeAtencionInterventionLevel,
} from "../../utils/reportTypeConstants";
import { vm } from "../../../../shared/utils/validationMessages";

export interface UseRegisterNewFormProps {
  onSubmit: (
    data: NewFormData | MaintenanceNewFormData | PoolRegulationNewFormData | AdverseEventNewFormData
  ) => void;
  reportTypes: ReportType[];
  maintenanceTypes?: MaintenanceType[];
  poolRegulations?: PoolRegulation[];
  interventionLevels?: InterventionLevel[];
  adverseEventTypes?: AdverseEventType[];
  initialData?: Partial<
    NewFormData | MaintenanceNewFormData | PoolRegulationNewFormData | AdverseEventNewFormData
  >;
  swimmer?: Swimmer;
}

const EVENT_TIME_MIN_MINUTES = 8 * 60; // 08:00
const EVENT_TIME_MAX_MINUTES = 18 * 60; // 18:00 inclusive

/**
 * Extracts minutes-from-midnight for validation.
 * - Form values ("YYYY-MM-DDTHH:mm") are already Colombia wall time.
 * - API ISO values (with Z/offset) are converted via Date to local time.
 */
const getEventTimeMinutes = (dateTime: string): number | null => {
  const trimmed = dateTime.trim();
  const isAbsoluteIso = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed);

  if (isAbsoluteIso) {
    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) return null;
    return date.getHours() * 60 + date.getMinutes();
  }

  const timePart = trimmed.includes("T") ? trimmed.split("T")[1] : trimmed;
  const match = timePart?.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
};

export const newFormBaseSchema = z.object({
  event_timestamp: z
    .string()
    .min(1, { message: vm.EVENT_TIMESTAMP_REQUIRED })
    .refine(
      (value) => {
        const minutes = getEventTimeMinutes(value);
        if (minutes === null) return false;
        return (
          minutes >= EVENT_TIME_MIN_MINUTES && minutes <= EVENT_TIME_MAX_MINUTES
        );
      },
      { message: vm.EVENT_TIMESTAMP_TIME_RANGE }
    ),
  report_type_id: z.string().min(1, { message: vm.REPORT_TYPE_REQUIRED }),
  event_description: z.string().min(1, { message: vm.EVENT_DESCRIPTION_REQUIRED }),
  security_officer_name: z
    .string()
    .max(150, { message: vm.SECURITY_OFFICER_NAME_MAX_LENGTH })
    .optional(),
  maintenance_type_id: z.string().optional(),
  pool_regulation_id: z.string().optional(),
  intervention_level_id: z.string().optional(),
  adverse_events_type_id: z.string().optional(),
  spent_items: z.string().nullable().optional(),
  event_main_factor_id: z.string().optional(),
  event_specific_factor_id: z.string().optional(),
  adverse_event_intervention_level_id: z.string().optional(),
});

/**
 * Creates a dynamic schema that validates maintenance_type_id when report_type_id is maintenance,
 * pool_regulation_id and intervention_level_id when report_type_id is pool regulation,
 * and spent_items when adverse event type is "Accidente".
 *
 * @param requireAdverseEventFactors - When true (new records), event_specific_factor_id and
 *   adverse_event_intervention_level_id are REQUIRED for Accidente/Incidente. When false (editing
 *   legacy records that predate this feature), the fields become optional — both must be provided
 *   together or both omitted, matching the backend update validation.
 */
export const createNewFormSchema = (
  reportTypes: ReportType[] | undefined,
  adverseEventTypes?: AdverseEventType[],
  interventionLevels?: InterventionLevel[],
  requireAdverseEventFactors: boolean = true
) => {
  return newFormBaseSchema.superRefine((data, ctx) => {
    const reportTypeId = data.report_type_id;
    
    // Check if intervention level is "Llamado de Atención 1" or "Llamado de Atención 2"
    const isLlamadoDeAtencion = isLlamadoDeAtencionInterventionLevel(
      data.intervention_level_id,
      interventionLevels
    );
    
    // security_officer_name is required for all report types EXCEPT:
    // 1. "Puesto de P.A / Botiquín"
    // 2. Pool regulation with "Llamado de Atención 1" or "Llamado de Atención 2" intervention levels
    const shouldSkipSecurityOfficerValidation = 
      isFirstAidStationReportType(reportTypeId, reportTypes) || isLlamadoDeAtencion;
    
    if (reportTypeId && !shouldSkipSecurityOfficerValidation) {
      if (!data.security_officer_name || data.security_officer_name.trim() === "") {
        ctx.addIssue({
          code: "custom",
          message: vm.SECURITY_OFFICER_NAME_REQUIRED,
          path: ["security_officer_name"],
        });
      }
    }
    
    // If report type is maintenance OR if it's a maintenance type value (e.g., "maintenance_1"),
    // maintenance_type_id is required (unless it's a maintenance type value, which already has the type)
    if (reportTypeId && isMaintenanceReportType(reportTypeId, reportTypes)) {
      if (!data.maintenance_type_id || data.maintenance_type_id.trim() === "") {
        ctx.addIssue({
          code: "custom",
          message: vm.MAINTENANCE_TYPE_REQUIRED,
          path: ["maintenance_type_id"],
        });
      }
    }
    // If it's a maintenance type value (e.g., "maintenance_1" for "Elementos de Emergencia"),
    // the maintenance_type_id will be extracted during form submission transformation

    // If report type is pool regulation, pool_regulation_id and intervention_level_id are required
    if (reportTypeId && isPoolRegulationReportType(reportTypeId, reportTypes)) {
      if (!data.pool_regulation_id || data.pool_regulation_id.trim() === "") {
        ctx.addIssue({
          code: "custom",
          message: vm.POOL_REGULATION_TYPE_REQUIRED,
          path: ["pool_regulation_id"],
        });
      }
      if (!data.intervention_level_id || data.intervention_level_id.trim() === "") {
        ctx.addIssue({
          code: "custom",
          message: vm.INTERVENTION_LEVEL_REQUIRED,
          path: ["intervention_level_id"],
        });
      }
    }

    // Check if this is an adverse event (either direct selection or via UI pattern)
    const isDirectAdverseEvent = reportTypeId && isAdverseEventReportType(reportTypeId, reportTypes);
    const isAdverseEventTypeSelection = reportTypeId && isAdverseEventTypeValue(reportTypeId);
    
    if (isDirectAdverseEvent || isAdverseEventTypeSelection) {
      // Determine the adverse event type ID to validate
      const adverseEventTypeIdToValidate = getAdverseEventTypeIdForValidation(
        reportTypeId,
        data.adverse_events_type_id
      );

      // Validate spent_items requirement for "Accidente" type
      if (adverseEventTypeIdToValidate && adverseEventTypes) {
        const selectedAdverseEventType = adverseEventTypes.find(
          (type) => type.id.toString() === adverseEventTypeIdToValidate
        );
        
        if (selectedAdverseEventType?.name === ADVERSE_EVENT_TYPE_NAMES.ACCIDENT) {
          if (!data.spent_items || data.spent_items.trim() === "") {
            ctx.addIssue({
              code: "custom",
              message: vm.SPENT_ITEMS_REQUIRED_FOR_ACCIDENT,
              path: ["spent_items"],
            });
          }
        }

        // event_specific_factor_id and adverse_event_intervention_level_id validation
        // for Accidente and Incidentes:
        //   - New records (requireAdverseEventFactors=true): both fields are REQUIRED.
        //   - Legacy edit (requireAdverseEventFactors=false): both must be provided together
        //     or both omitted. Partial submission is always invalid.
        if (
          selectedAdverseEventType?.name === ADVERSE_EVENT_TYPE_NAMES.ACCIDENT ||
          selectedAdverseEventType?.name === ADVERSE_EVENT_TYPE_NAMES.INCIDENTS
        ) {
          const hasFactor = !!data.event_specific_factor_id?.trim();
          const hasLevel  = !!data.adverse_event_intervention_level_id?.trim();

          if (requireAdverseEventFactors) {
            if (!hasFactor) {
              ctx.addIssue({
                code: "custom",
                message: vm.EVENT_SPECIFIC_FACTOR_REQUIRED,
                path: ["event_specific_factor_id"],
              });
            }
            if (!hasLevel) {
              ctx.addIssue({
                code: "custom",
                message: vm.ADVERSE_EVENT_INTERVENTION_LEVEL_REQUIRED,
                path: ["adverse_event_intervention_level_id"],
              });
            }
          } else {
            // Edit mode: allow both empty (legacy) but reject partial (one set, other missing).
            if (hasFactor !== hasLevel) {
              const missingPath = hasFactor
                ? "adverse_event_intervention_level_id"
                : "event_specific_factor_id";
              ctx.addIssue({
                code: "custom",
                message: hasFactor
                  ? vm.ADVERSE_EVENT_INTERVENTION_LEVEL_REQUIRED
                  : vm.EVENT_SPECIFIC_FACTOR_REQUIRED,
                path: [missingPath],
              });
            }
          }
        }
      }
    }
  });
};

/**
 * Helper function to extract the adverse event type ID for validation purposes.
 * Handles both direct adverse event selection and UI pattern with prefixed IDs.
 */
function getAdverseEventTypeIdForValidation(
  reportTypeId: string,
  adverseEventsTypeId?: string
): string | undefined {
  // If using UI pattern (prefixed ID), extract the adverse event type ID
  if (isAdverseEventTypeValue(reportTypeId)) {
    try {
      return extractAdverseEventTypeId(reportTypeId);
    } catch (error) {
      console.error("Failed to extract adverse event type ID during validation:", error);
      return undefined;
    }
  }
  
  // Otherwise, use the adverse_events_type_id field (direct selection)
  return adverseEventsTypeId;
}

export type NewFormData = z.infer<typeof newFormBaseSchema>;

/**
 * Extended form data for maintenance news, includes maintenance_type_id.
 */
export interface MaintenanceNewFormData extends NewFormData {
  maintenance_type_id: string;
}

/**
 * Extended form data for pool regulation news, includes pool_regulation_id and intervention_level_id.
 */
export interface PoolRegulationNewFormData extends NewFormData {
  pool_regulation_id: string;
  intervention_level_id: string;
}

/**
 * Extended form data for adverse event news, includes adverse_events_type_id.
 */
export interface AdverseEventNewFormData extends NewFormData {
  adverse_events_type_id: string;
  spent_items?: string | null;
  event_main_factor_id?: string;
  event_specific_factor_id?: string;
  adverse_event_intervention_level_id?: string;
}

